import { CURRENCY } from '@/app/constants';
import type { SupportedLanguage } from '@/app/types';

export const formatPriceDiscount = (discount: number, locale: SupportedLanguage) => {
  return locale === 'zh' ? (100 - discount) / 10 : discount;
};

export const getPriceDiscount = (price: number, originalPrice: number) => {
  return originalPrice > price ? 100 - Math.round((price / originalPrice) * 100) : 0;
};

type DiscountProduct = {
  price?: number;
  original_price?: number;
  plan_type?: string;
  credits_monthly_gift?: number;
  billing_period?: string;
  product_type?: string;
};

export const getProductVariantKey = (product: DiscountProduct): string =>
  `${product.plan_type ?? ''}:${product.credits_monthly_gift ?? ''}`;

/** 各 plan_type + credits_monthly_gift 档位的月付原价索引。 */
export type MonthlyOriginalByVariant = Map<string, number>;

/**
 * 建立「同档位月付原价」索引，键为 plan_type + credits_monthly_gift。
 * 旗舰版同 period 有多个积分档位，必须按积分区分，不能只按 plan_type 覆盖。
 */
export const buildMonthlyOriginalByVariant = (
  products: DiscountProduct[],
  productType: 'subscription' | 'pass',
): MonthlyOriginalByVariant =>
  new Map(
    products
      .filter((p) => p.product_type === productType && p.billing_period === 'monthly' && p.plan_type)
      .map((p) => [getProductVariantKey(p), p.original_price ?? p.price ?? 0]),
  );

/**
 * 卡面划线价的基准来源：键为 `${product_type}:${billing_period}`，
 * 值为「同档位月付 SKU 的类型 + 该档位原价的倍数」。
 * 划线价体现「按月购买原价」，与真实扣款的 original_price 解耦——后者仍供
 * 「下个月续费金额」等文案使用。未命中的组合（如国内按月购买本身）直接用自己的原价。
 */
const STRIKETHROUGH_BASELINE: Record<
  'china' | 'global',
  Partial<Record<string, { ref: 'subscription' | 'pass'; months: number }>>
> = {
  china: {
    'subscription:monthly': { ref: 'pass', months: 1 },
    'subscription:yearly': { ref: 'pass', months: 12 },
    'pass:yearly': { ref: 'pass', months: 12 },
  },
  global: {
    'subscription:yearly': { ref: 'subscription', months: 12 },
  },
};

/**
 * 计算卡面划线价（分）。规则：
 * - 国内连续包月：同档位「按月购买」SKU 原价；
 * - 国内按年购买 / 连续包年：同档位「按月购买」SKU 原价 × 12（卡面再除以 12 显示月均原价）；
 * - 海外连续包年：同档位「连续包月」SKU 原价 × 12；
 * - 其余组合：商品自身 original_price。
 * 匹配不到同档位月付 SKU 时回落到自身原价，避免划线价凭空消失。
 */
export const getStrikethroughPrice = (
  product: DiscountProduct,
  monthlyOriginals: Record<'subscription' | 'pass', MonthlyOriginalByVariant>,
  site: 'china' | 'global',
): number => {
  const ownOriginalPrice = product.original_price ?? 0;
  const baseline = STRIKETHROUGH_BASELINE[site][`${product.product_type}:${product.billing_period}`];
  if (!baseline) return ownOriginalPrice;

  const monthlyOriginal = monthlyOriginals[baseline.ref].get(getProductVariantKey(product));
  // 0 视为「无该档位基准」：用 0 当划线价会渲染出比实付还低的假原价。
  return !monthlyOriginal ? ownOriginalPrice : monthlyOriginal * baseline.months;
};

/**
 * 计算指定商品类型的包年最大折扣百分比。
 * 包年套餐标签使用「包年价 vs 同档位月付原价×12」的综合口径。
 */
export const getMaxYearlyProductDiscount = (
  products: DiscountProduct[],
  productType: 'subscription' | 'pass',
): number => {
  const monthlyOriginalByPlan = new Map<string, number>();
  const yearlyProductByPlan = new Map<string, DiscountProduct>();
  for (const product of products) {
    if (product.product_type === productType && product.billing_period === 'monthly' && product.plan_type) {
      monthlyOriginalByPlan.set(getProductVariantKey(product), product.original_price ?? product.price ?? 0);
    }
    if (product.product_type === productType && product.billing_period === 'yearly' && product.plan_type) {
      // 同档位多个积分 SKU 必须分别计算，不能只按 plan_type 覆盖。
      yearlyProductByPlan.set(getProductVariantKey(product), product);
    }
  }

  let max = 0;

  for (const product of yearlyProductByPlan.values()) {
    const monthlyBaseline = (monthlyOriginalByPlan.get(getProductVariantKey(product)) ?? 0) * 12;
    const price = product.price ?? 0;
    if (monthlyBaseline > 0 && price < monthlyBaseline) {
      max = Math.max(max, 100 - Math.round((price / monthlyBaseline) * 100));
    }
  }
  return max;
};

const getMaxMonthlyProductDiscount = (
  products: DiscountProduct[],
  productType: 'subscription' | 'pass',
  referenceProductType?: 'subscription' | 'pass',
): number => {
  const referenceOriginalByPlan = new Map<string, number>();
  if (referenceProductType) {
    for (const product of products) {
      if (product.product_type === referenceProductType && product.billing_period === 'monthly' && product.plan_type) {
        referenceOriginalByPlan.set(getProductVariantKey(product), product.original_price ?? product.price ?? 0);
      }
    }
  }

  let max = 0;
  for (const product of products) {
    if (product.product_type !== productType || product.billing_period !== 'monthly') continue;
    const originalPrice = referenceProductType
      ? (referenceOriginalByPlan.get(getProductVariantKey(product)) ?? 0)
      : (product.original_price ?? 0);
    max = Math.max(max, getPriceDiscount(product.price ?? 0, originalPrice));
  }
  return max;
};

/**
 * 计算侧边栏首购优惠展示的最大折扣，口径与订阅页各购买周期的折扣标签一致。
 */
export const getMaxSidebarDiscount = (products: DiscountProduct[], site: 'china' | 'global'): number => {
  if (site === 'china') {
    return Math.max(
      getMaxMonthlyProductDiscount(products, 'pass'),
      getMaxMonthlyProductDiscount(products, 'subscription', 'pass'),
      getMaxYearlyProductDiscount(products, 'pass'),
    );
  }

  return Math.max(
    getMaxMonthlyProductDiscount(products, 'subscription'),
    getMaxYearlyProductDiscount(products, 'subscription'),
  );
};

/**
 * 计算一组商品的最大折扣百分比（营销文案「最高X折」用）。
 * 规则：
 * - 单品折扣：getPriceDiscount(price, original_price)，price 为券后价，故已含券折扣
 * - 年付综合折扣：年付券后价 vs 同 plan 月付原价×12，体现「年付优惠 + 券」叠加
 * 取全部候选的最大值。
 */
export const getMaxProductDiscount = (products: DiscountProduct[]): number => {
  // 同 plan 月付原价，作为年付综合折扣的分母基准
  const monthlyOriginalByPlan = new Map<string, number>();
  for (const p of products) {
    if (p.billing_period === 'monthly' && p.plan_type) {
      monthlyOriginalByPlan.set(p.plan_type, p.original_price ?? p.price ?? 0);
    }
  }

  let max = 0;
  for (const p of products) {
    const price = p.price ?? 0;
    // 单品折扣（含券）
    max = Math.max(max, getPriceDiscount(price, p.original_price ?? 0));
    // 年付综合折扣（年付优惠 + 券）
    if (p.billing_period === 'yearly' && p.plan_type) {
      const monthlyBaseline = (monthlyOriginalByPlan.get(p.plan_type) ?? 0) * 12;
      if (monthlyBaseline > 0 && price < monthlyBaseline) {
        max = Math.max(max, 100 - Math.round((price / monthlyBaseline) * 100));
      }
    }
  }
  return max;
};

/**
 * 价格金额格式化（输入为「分」，输出纯数字字符串，不带货币符号）。
 * 规则：无小数→整数；有小数→保留两位并去末尾零。
 *   135800 → "1358"   2110 → "21.1"   2101 → "21.01"   1 → "0.01"   0 → "0"
 * 用于货币符号需单独排版（如大字号金额旁的小字号符号）的场景。
 */
export const formatAmount = (cents: number): string => {
  return (Math.round(cents) / 100).toFixed(2).replace(/\.?0+$/, '');
};

/**
 * 价格格式化（输入为「分」，输出带货币符号）。
 *   135800 → "¥1358"   2110 → "¥21.1"   1 → "¥0.01"   0 → "¥0"
 */
export const formatPrice = (cents: number): string => `${CURRENCY}${formatAmount(cents)}`;

/**
 * 价格金额格式化（输入为「分」，输出千分位分组的数字字符串，不带货币符号）。
 * 规则：整数部分按千分位分组；小数保留两位并去末尾零。
 *   864000 → "8,640"   4286000 → "42,860"   2110 → "21.1"   0 → "0"
 */
export const formatAmountGrouped = (cents: number): string => {
  const [intPart, decimalPart] = (Math.round(cents) / 100).toFixed(2).split('.');
  const groupedInt = Number(intPart).toLocaleString('en-US');
  const trimmedDecimal = decimalPart.replace(/0+$/, '');
  return trimmedDecimal ? `${groupedInt}.${trimmedDecimal}` : groupedInt;
};

/**
 * 价格格式化（输入为「分」，输出带货币符号 + 千分位分组）。
 *   864000 → "¥8,640"   4286000 → "¥42,860"   199900 → "¥1,999"   0 → "¥0"
 */
export const formatPriceGrouped = (cents: number): string => `${CURRENCY}${formatAmountGrouped(cents)}`;
